import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { TextSkeleton } from './SkeletonLoaders';
import { config } from '../config';
import { withImpersonationHeader } from '../utils/impersonation';

// Custom styles for the slider
const sliderStyles = `
  .slider-input::-webkit-slider-thumb {
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1, #3b82f6);
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    transition: all 0.2s ease;
    border: 2px solid #1f2937;
  }
  
  .slider-input::-webkit-slider-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.6);
  }
  
  .slider-input::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1, #3b82f6);
    cursor: pointer;
    border: 2px solid #1f2937;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    transition: all 0.2s ease;
  }
  
  .slider-input::-moz-range-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.6);
  }
`;

interface PricingTier {
  plan_id: string;
  name: string;
  description: string;
  credits_limit: number;
  monthly_cost: number;
}

interface PricingSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onPlanSelect: (plan: PricingTier) => void;
  currentPlan?: PricingTier;
  loading?: boolean;
}

const PricingSlider: React.FC<PricingSliderProps> = ({ 
  isOpen, 
  onClose, 
  onPlanSelect, 
  currentPlan,
  loading = false
}) => {
  const { session } = useAuth();
  const [plans, setPlans] = useState<PricingTier[]>([]);
  const [sliderValue, setSliderValue] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<PricingTier | null>(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansCached, setPlansCached] = useState(false);
  const [cacheTimestamp, setCacheTimestamp] = useState<number>(0);

  // Fetch available plans with caching
  const fetchPlans = useCallback(async (forceRefresh = false) => {
    if (!session?.access_token) return;
    
    // Check cache validity (5 minutes)
    const now = Date.now();
    const cacheValid = plansCached && (now - cacheTimestamp) < 5 * 60 * 1000; // 5 minutes
    
    if (cacheValid && !forceRefresh && plans.length > 0) {
      console.log('📦 Using cached plans data');
      return;
    }
    
    setPlansLoading(true);
    try {
      console.log('🔄 Fetching fresh plans data...');
      const response = await fetch(config.getApiUrl('/subscription/plans'), {
        headers: withImpersonationHeader({
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
        setPlansCached(true);
        setCacheTimestamp(now);
        
        console.log('✅ Plans cached successfully');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setPlansLoading(false);
    }
  }, [session?.access_token, plansCached, cacheTimestamp, plans.length]);

  // Initialize slider based on current plan
  const initializeSlider = useCallback(() => {
    if (currentPlan && plans.length > 0) {
      const currentIndex = plans.findIndex((p: PricingTier) => p.plan_id === currentPlan.plan_id);
      if (currentIndex !== -1) {
        setSliderValue(currentIndex);
        setSelectedPlan(plans[currentIndex]);
      }
    } else if (plans.length > 0) {
      setSliderValue(0);
      setSelectedPlan(plans[0]);
    }
  }, [currentPlan, plans]);

  // Fetch plans when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen, fetchPlans]);

  // Initialize slider when plans or currentPlan changes
  useEffect(() => {
    initializeSlider();
  }, [initializeSlider]);

  // Handle slider change
  const handleSliderChange = (value: number) => {
    setSliderValue(value);
    if (plans[value]) {
      setSelectedPlan(plans[value]);
    }
  };

  // Calculate discounted price for annual billing
  const getPrice = (plan: PricingTier) => {
    const monthlyPrice = plan.monthly_cost;
    return isAnnual ? monthlyPrice * 0.8 : monthlyPrice; // 20% discount for annual
  };

  const getAnnualPrice = (plan: PricingTier) => {
    return getPrice(plan) * 12;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <style dangerouslySetInnerHTML={{ __html: sliderStyles }} />
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="flex items-center justify-between p-8 pb-4">
          <div>
            <h2 className="text-2xl font-bold text-zinc-100">Choose Your Plan</h2>
            <p className="text-zinc-400 mt-1">Select the perfect plan for your needs</p>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="px-8 overflow-y-auto flex-1">

        {/* Billing Toggle */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center bg-zinc-800 rounded-lg p-1">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                !isAnnual 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all relative ${
                isAnnual 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Annual
              <span className="absolute -top-2 -right-1 bg-green-500 text-xs text-white px-1.5 py-0.5 rounded-full">
                20% OFF
              </span>
            </button>
          </div>
        </div>

        {/* Loading Overlay for Plan Updates */}
        {loading && (
          <div className="absolute inset-0 bg-zinc-900/98 backdrop-blur-sm z-50 flex items-center justify-center rounded-2xl">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-6"></div>
              <h3 className="text-xl font-semibold text-zinc-100 mb-2">Updating your plan...</h3>
              <p className="text-zinc-400">This will just take a moment</p>
            </div>
          </div>
        )}

        {plansLoading ? (
          <>
            {/* Slider skeleton */}
            <div className="mb-12">
              <div className="px-4">
                <div className="relative">
                  <div className="w-full h-1.5 bg-zinc-800 rounded-full"></div>
                  <div className="flex justify-between mt-4">
                    {[1,2,3,4,5].map((i) => (
                      <div key={i} className="flex flex-col items-center">
                        <div className="w-4 h-4 bg-zinc-800 rounded-full"></div>
                        <div className="mt-3 text-center">
                          <TextSkeleton width="full" size="sm" />
                          <TextSkeleton width="3/4" size="sm" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Plan card skeleton */}
            <div className="bg-zinc-800/20 border border-zinc-700/50 rounded-2xl p-8 mb-8">
              <div className="text-center space-y-4">
                <div className="w-32 h-8 bg-zinc-800 rounded-full mx-auto"></div>
                <TextSkeleton width="1/2" size="xl" className="mx-auto" />
                <TextSkeleton width="3/4" size="base" lines={2} className="mx-auto" />
                
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <TextSkeleton width="1/2" size="xl" />
                    <TextSkeleton width="full" size="sm" />
                  </div>
                  <div className="w-px h-12 bg-zinc-700" />
                  <div className="text-center">
                    <TextSkeleton width="3/4" size="lg" />
                    <TextSkeleton width="full" size="sm" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Button skeleton */}
            <div className="flex gap-4 justify-center mt-8 pt-6 border-t border-zinc-800 bg-zinc-900 pb-4">
              <div className="w-24 h-12 bg-zinc-800 rounded-xl"></div>
              <div className="w-32 h-12 bg-zinc-800 rounded-xl"></div>
            </div>
          </>
        ) : plans.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-zinc-400 mb-4">No plans available</p>
            <button 
              onClick={() => fetchPlans(true)}
              className="text-blue-400 hover:text-blue-300 underline"
            >
              Retry loading plans
            </button>
          </div>
        ) : (
          <>
            {/* Interactive Slider */}
            <div className="mb-12">
              <div className="px-4">
                <div className="relative">
                  {/* Slider Track */}
                  <div className="w-full h-1.5 bg-zinc-800 rounded-full relative shadow-inner">
                    <div 
                      className="h-1.5 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500 rounded-full transition-all duration-500 ease-out shadow-sm"
                      style={{ width: `${(sliderValue / (plans.length - 1)) * 100}%` }}
                    />
                  </div>
                  
                  {/* Slider Input */}
                  <input
                    type="range"
                    min="0"
                    max={plans.length - 1}
                    value={sliderValue}
                    onChange={(e) => handleSliderChange(parseInt(e.target.value))}
                    className="absolute top-0 w-full h-1.5 bg-transparent appearance-none cursor-pointer slider-input"
                    style={{
                      background: 'transparent',
                      WebkitAppearance: 'none',
                      height: '6px',
                      outline: 'none',
                    }}
                  />
                  
                  {/* Notches */}
                  <div className="flex justify-between mt-4">
                    {plans.map((plan, index) => (
                      <div 
                        key={plan.plan_id} 
                        className="flex flex-col items-center cursor-pointer"
                        onClick={() => handleSliderChange(index)}
                      >
                        <div 
                          className={`w-4 h-4 rounded-full border-2 transition-all duration-300 shadow-sm ${
                            index === sliderValue 
                              ? 'bg-gradient-to-br from-indigo-400 to-blue-500 border-indigo-400 scale-125 shadow-indigo-500/30' 
                              : 'bg-zinc-800 border-zinc-600 hover:border-indigo-400 hover:scale-110'
                          }`}
                        />
                        <div className="mt-3 text-center">
                          <div className={`text-sm font-semibold transition-all duration-300 ${
                            index === sliderValue ? 'text-indigo-300' : 'text-zinc-400'
                          }`}>
                            {plan.credits_limit.toLocaleString()}
                          </div>
                          <div className={`text-xs transition-all duration-300 ${
                            index === sliderValue ? 'text-zinc-300' : 'text-zinc-500'
                          }`}>
                            credits/mo
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Plan Card */}
            {selectedPlan && (
              <div className="bg-gradient-to-br from-indigo-500/10 via-blue-500/5 to-cyan-500/10 border border-indigo-500/30 rounded-2xl p-8 mb-8 shadow-2xl backdrop-blur-sm">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500/20 to-blue-500/20 text-indigo-300 px-4 py-2 rounded-full text-sm font-semibold mb-6 shadow-lg backdrop-blur">
                    {selectedPlan.plan_id === currentPlan?.plan_id ? 'Current Plan' : 'Selected Plan'}
                  </div>
                  
                  <h3 className="text-3xl font-bold text-zinc-100 mb-2">
                    {selectedPlan.name}
                  </h3>
                  
                  <p className="text-zinc-400 mb-6 text-lg">
                    {selectedPlan.description}
                  </p>
                  
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-zinc-100">
                        ${getPrice(selectedPlan).toFixed(0)}
                      </div>
                      <div className="text-zinc-400 text-sm">
                        per {isAnnual ? 'month, billed annually' : 'month'}
                      </div>
                      {isAnnual && (
                        <div className="text-green-400 text-sm mt-1">
                          ${getAnnualPrice(selectedPlan).toFixed(0)} billed annually
                        </div>
                      )}
                    </div>
                    
                    <div className="w-px h-12 bg-zinc-700" />
                    
                    <div className="text-center">
                      <div className="text-2xl font-semibold text-zinc-200">
                        {selectedPlan.credits_limit.toLocaleString()}
                      </div>
                      <div className="text-zinc-400 text-sm">
                        Credits per month
                      </div>
                    </div>
                  </div>

                  {/* Features List */}
                  {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    <div className="text-left space-y-3">
                      <div className="flex items-center gap-3 text-zinc-200">
                        <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                          <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span>Dynamic API routes</span>
                      </div>
                      <div className="flex items-center gap-3 text-zinc-200">
                        <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                          <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span>Usage analytics & monitoring</span>
                      </div>
                      <div className="flex items-center gap-3 text-zinc-200">
                        <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                          <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span>24/7 API availability</span>
                      </div>
                    </div>
                    <div className="text-left space-y-3">
                      <div className="flex items-center gap-3 text-zinc-200">
                        <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                          <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span>Secure authentication</span>
                      </div>
                      <div className="flex items-center gap-3 text-zinc-200">
                        <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                          <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span>Developer support</span>
                      </div>
                      <div className="flex items-center gap-3 text-zinc-200">
                        <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                          <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span>Monthly usage reports</span>
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>
            )}

          </>
        )}
        </div>

        {/* Action Buttons - Outside scrollable area */}
        {!loading && (
          <div className="flex gap-4 justify-center pt-6 pb-8 px-8 border-t border-zinc-700/50 bg-zinc-900 rounded-b-2xl">
            <button
              onClick={onClose}
              className="px-8 py-3 border border-zinc-600 rounded-xl text-zinc-300 hover:bg-zinc-800 hover:border-zinc-500 transition-all duration-200 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={() => selectedPlan && onPlanSelect(selectedPlan)}
              disabled={!selectedPlan || selectedPlan.plan_id === currentPlan?.plan_id}
              className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {selectedPlan?.plan_id === currentPlan?.plan_id ? (
                'Current Plan'
              ) : (
                `Select ${selectedPlan?.name || 'Plan'}`
              )}
            </button>
          </div>
        )}
      </div>
      
    </div>
  );
};

export default PricingSlider;
